import React, { FC, useRef, useState } from 'react';
import { 
  Film,
  Library,
  PersonStanding,
  GraduationCap,
  Settings,
  Plus,
  ChevronRight,
  Menu,
  X,
  LogIn,
  LogOut,
  User,
  Info,
  CloudDownload
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useDanceStore } from '../store/useDanceStore';
import { useStorageStore } from '../store/useStorageStore';
import { useTranslation } from 'react-i18next';
import { DanceState, ModuleType } from '../types';
import { TUTORIAL_MODULES } from '../utils/tutorialData';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuthStore } from '../store/useAuthStore';
import { AuthModal } from './AuthModal';

export const MainNavigation: FC = () => {
  const { t } = useTranslation();
  const { isMobile, isLandscapeMobile } = useIsMobile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const [showAuth, setShowAuth] = useState(false);
  const [showUserTooltip, setShowUserTooltip] = useState(false);

  const { user, logout, tier } = useAuthStore();
  const activeModule = useDanceStore((state: DanceState) => state.activeModule);
  const setActiveModule = useDanceStore((state: DanceState) => state.setActiveModule);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const setIsFeedbackOpen = useDanceStore((state: DanceState) => state.setIsFeedbackOpen);
  const confirmAction = useDanceStore((state: DanceState) => state.confirmAction);
  const newSequence = useDanceStore((state: DanceState) => state.newSequence);
  const setDuration = useDanceStore((state: DanceState) => state.setDuration);
  const hasUnsavedSequenceChanges = useDanceStore((state: DanceState) => state.hasUnsavedSequenceChanges);
  const hasUnsavedChanges = useDanceStore((state: DanceState) => state.hasUnsavedChanges);
  const loadedSequence = useDanceStore((state: DanceState) => state.loadedSequence);
  
  const tutorialProgress = useStorageStore(state => state.tutorialProgress);
  const updateTutorialProgress = useStorageStore(state => state.updateTutorialProgress);

  const handleModuleChange = (mod: ModuleType) => {
    if (activeModule === mod) return;

    // Check if the current tutorial step requires switching to this specific module/tab
    const currentModule = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId);
    const currentStep = currentModule?.steps[tutorialProgress.currentStepIndex];
    const isSwitchingRequiredTab = currentStep?.actionRequired === 'switchTab' && currentStep?.targetValue === mod;

    if (tutorialProgress.currentModuleId && !tutorialProgress.completedModuleIds.includes(tutorialProgress.currentModuleId) && !isSwitchingRequiredTab) {
      confirmAction({
        title: t('app.abandon_tutorial_title'),
        message: t('app.abandon_tutorial_message'),
        onConfirm: () => {
          updateTutorialProgress({ currentModuleId: null, currentStepIndex: 0 });
          setDuration(10);
          setActiveModule(mod);
        }
      });
    } else {
      setActiveModule(mod);
    }
  };

  const handleNewSequence = () => {
    if (hasUnsavedSequenceChanges || hasUnsavedChanges) {
      confirmAction({
        title: t('library.new_sequence_confirm_title'),
        message: t('library.new_sequence_confirm_message'),
        confirmLabel: t('library.discard_new'),
        onConfirm: () => {
          newSequence();
          setActiveModule('PoseDesigner');
        },
      });
    } else if (loadedSequence) {
      confirmAction({
        title: t('library.new_sequence_loaded_title'),
        message: t('library.new_sequence_loaded_message'),
        confirmLabel: t('library.new_sequence_continue'),
        onConfirm: () => {
          newSequence();
          setActiveModule('PoseDesigner');
        },
      });
    } else {
      newSequence();
      setActiveModule('PoseDesigner');
    }
  };

if (isMobile) {
    return (
      <>
        {/* Beautiful floating Glassmorphic Hamburger button */}
        {!isLandscapeMobile && <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="mobile-hamburger-btn"
        >
          {isMobileMenuOpen ? <X size={18} style={{ color: '#38bdf8' }} /> : <Menu size={18} style={{ color: '#38bdf8' }} />}
        </button>}

        {/* Vertical menu dropdown overlay */}
        {!isLandscapeMobile && isMobileMenuOpen && (
          <div className="mobile-navigation-dropdown">
            <NavItem
              icon={<PersonStanding size={18} />}
              label={t('nav.viewer', 'Viewer')}
              active={activeModule === 'PoseDesigner'}
              onClick={() => {
                handleModuleChange('PoseDesigner');
                setIsMobileMenuOpen(false);
              }}
              isExpanded={true}
            />
            <NavItem
              id="nav-sequences-tab"
              icon={<Film size={18} />}
              label={t('nav.sequences')}
              active={activeModule === 'Sequences'}
              onClick={() => {
                handleModuleChange('Sequences');
                setIsMobileMenuOpen(false);
              }}
              isExpanded={true}
            />
            <NavItem
              id="nav-poses-tab"
              icon={<Library size={18} />}
              label={t('nav.poses')}
              active={activeModule === 'Poses'}
              onClick={() => {
                handleModuleChange('Poses');
                setIsMobileMenuOpen(false);
              }}
              isExpanded={true}
            />
            <NavItem
              icon={<Settings size={18} />}
              label={t('nav.settings')}
              active={activeModule === 'Settings'}
              onClick={() => {
                handleModuleChange('Settings');
                setIsMobileMenuOpen(false);
              }}
              isExpanded={true}
            />
            <NavItem
              icon={<CloudDownload size={18} />}
              label={t('nav.import_export', 'Import / Export')}
              active={activeModule === 'ImportExport'}
              onClick={() => {
                handleModuleChange('ImportExport');
                setIsMobileMenuOpen(false);
              }}
              isExpanded={true}
            />
            {user && (
              <NavItem 
                icon={<User size={18} />} 
                label={t('nav.profile', 'Profile')} 
                active={activeModule === 'Profile'} 
                onClick={() => {
                  handleModuleChange('Profile');
                  setIsMobileMenuOpen(false);
                }}
                isExpanded={true}
              />
            )}
            
            {/* Sign In / Sign Out */}
            {user ? (
              <NavItem 
                icon={<LogOut size={18} />} 
                label={t('nav.sign_out')}
                onClick={async () => { 
                  await logout();
                  window.location.reload();
                }}
                isExpanded={true}
              />
            ) : (
              <NavItem 
                icon={<LogIn size={18} />} 
                label={t('auth.signin')}
                onClick={() => {
                  setShowAuth(true);
                  setIsMobileMenuOpen(false);
                }}
                isExpanded={true}
              />
            )}

            <NavItem
              id="nav-info-btn"
              icon={<Info size={18} />}
              label={t('nav.information', 'Information')}
              active={activeModule === 'Information'}
              onClick={() => {
                handleModuleChange('Information');
                setIsMobileMenuOpen(false);
              }}
              isExpanded={true}
            />

            <NavItem
              icon={<FeedbackDot />}
              label={t('nav.feedback')}
              onClick={() => { setIsFeedbackOpen(true); setIsMobileMenuOpen(false); }}
              color="#5eead4"
              isExpanded={true}
            />
          </div>
        )}

        {showAuth && createPortal(
          <AuthModal onClose={() => setShowAuth(false)} />,
          document.body
        )}
      </>
    );
  }

  return (
    <>
      <div 
        className={`nav-rail ${isExpanded ? 'expanded' : ''}`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => {
          setIsExpanded(false);
          setShowUserTooltip(false);
        }}
      >
      <div className="rail-group">
        <NavItem
          icon={<PersonStanding size={20} />}
          label={t('nav.editor')}
          active={activeModule === 'PoseDesigner'}
          onClick={() => handleModuleChange('PoseDesigner')}

          isExpanded={isExpanded}
        />
        <NavItem
          id="nav-sequences-tab"
          icon={<Film size={20} />}
          label={t('nav.sequences')}
          active={activeModule === 'Sequences'}
          onClick={() => handleModuleChange('Sequences')}

          isExpanded={isExpanded}
        />
        <NavItem
          id="nav-poses-tab"
          icon={<Library size={20} />}
          label={t('nav.poses')}
          active={activeModule === 'Poses'}
          onClick={() => handleModuleChange('Poses')}

          isExpanded={isExpanded}
        />
        <NavItem
          id="top-menu-tutorial"
          icon={<GraduationCap size={20} />} 
          label={t('nav.tutorial')} 
          active={activeModule === 'Tutorial'} 
          onClick={() => handleModuleChange('Tutorial')}
          
          isExpanded={isExpanded}
        />
        <NavItem
          icon={<Settings size={20} />} 
          label={t('nav.settings')} 
          active={activeModule === 'Settings'} 
          onClick={() => handleModuleChange('Settings')}
          
          isExpanded={isExpanded}
        />
      </div>

      <div className="rail-divider" />

      <div className="rail-group">
        <NavItem 
          id="nav-new-sequence-btn"
          icon={<Plus size={20} />} 
          label={t('nav.new_sequence')}
          onClick={handleNewSequence}
          
          isExpanded={isExpanded}
        />

        <div id="nav-import-export-container" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <NavItem
            id="nav-import-export-btn"
            icon={<CloudDownload size={20} />}
            label={t('nav.import_export', 'Import / Export')}
            active={activeModule === 'ImportExport'}
            onClick={() => handleModuleChange('ImportExport')}
            
            isExpanded={isExpanded}
          />
        </div>
      </div>

      <div className="rail-spacer" />

      <div className="rail-group">
        {user ? (
          <>
            <NavItem 
              icon={<User size={20} />} 
              label={t('nav.profile', 'Profile')} 
              active={activeModule === 'Profile'}
              onClick={() => handleModuleChange('Profile')}
              onMouseEnter={() => {
                
                setShowUserTooltip(true);
              }}
              onMouseLeave={() => setShowUserTooltip(false)}
              color="#2dd4bf"
              isExpanded={isExpanded}
            />
            <NavItem 
              icon={<LogOut size={20} />} 
              label={t('nav.sign_out')}
              onClick={async () => { await logout(); window.location.reload(); }}
              onMouseEnter={() => {
                
                setShowUserTooltip(false);
              }}
              isExpanded={isExpanded}
            />
          </>
        ) : (
          <NavItem 
            icon={<LogIn size={20} />} 
            label={t('auth.signin')}
            onClick={() => setShowAuth(true)}
            
            isExpanded={isExpanded}
          />
        )}

        <NavItem
          id="nav-info-btn"
          icon={<Info size={20} />}
          label={t('nav.information', 'Information')}
          active={activeModule === 'Information'}
          onClick={() => handleModuleChange('Information')}
          
          isExpanded={isExpanded}
        />

        <div className="rail-divider" />

        <NavItem
          id="nav-feedback"
          icon={<FeedbackDot />}
          label={t('nav.feedback')}
          onClick={() => setIsFeedbackOpen(true)}

          color="#5eead4"
          isExpanded={isExpanded}
        />
      </div>

      {showAuth && createPortal(
        <AuthModal onClose={() => setShowAuth(false)} />,
        document.body
      )}

      </div>

      {showUserTooltip && user && (
        <div 
          className="nav-profile-tooltip"
          style={{ left: isExpanded ? '176px' : '48px' }}
        >
          <div>
            <div style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('nav.signed_in_as')}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#2dd4bf', fontWeight: 600, wordBreak: 'break-all' }}>
              {user.email}
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '6px', marginTop: '4px' }}>
            <div style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('profile.plan')}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#2dd4bf', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              {t('profile.plan_' + tier, { defaultValue: tier })}
            </div>
          </div>
        </div>
      )}

    </>
  );
};

const NavItem: FC<{ 
  id?: string;
  icon: React.ReactNode; 
  label: string; 
  active?: boolean; 
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  highlightColor?: string;
  color?: string;
  isExpanded: boolean;
  chevron?: boolean;
  isSubItem?: boolean;
  disabled?: boolean;
  title?: string;
}> = ({ id, icon, label, active, onClick, onMouseEnter, onMouseLeave, highlightColor, color, isExpanded, chevron, isSubItem, disabled, title }) => {
  const activeClass = active ? 'active' : '';
  const subItemClass = isSubItem ? 'sub-item' : '';
  const disabledClass = disabled ? 'disabled' : '';
  
  return (
    <button 
      className={`nav-item ${activeClass} ${subItemClass} ${disabledClass}`} 
      onClick={disabled ? undefined : onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        ...color ? { color } : {},
        ...disabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}
      }}
      title={disabled ? undefined : (!isExpanded ? label : undefined)}
      aria-disabled={disabled}
    >
      <div id={id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <span>{label}</span>
      {chevron !== undefined && isExpanded && (
        <ChevronRight 
          size={12} 
          style={{ 
            marginLeft: 'auto', 
            opacity: 0.5, 
            transform: chevron ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }} 
        />
      )}
    </button>
  );
};

const FeedbackDot: FC = () => (
  <div className="feedback-dot-container">
    <div className="feedback-pulse" />
    <div className="feedback-inner-dot" />
  </div>
);
